import { Component, Input, Output, EventEmitter, OnDestroy, forwardRef, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule, NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, switchMap, tap, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TypeAheadConfig, TypeAheadOption, KeyboardKeys } from '@org/interfaces';

@Component({
    selector: 'lib-type-ahead',
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './typeAhead.component.html',
    styleUrl: './typeAhead.component.scss',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TypeAheadComponent),
            multi: true
        }
    ]
})
export class TypeAheadComponent<T> implements ControlValueAccessor, OnDestroy {
  @Input() config!: TypeAheadConfig<T>;
  @Output() selected = new EventEmitter<T>();
  @Output() selectedMultiple = new EventEmitter<T[]>();

  searchControl = new FormControl('');
  options: TypeAheadOption<T>[] = [];
  isLoading = false;
  private destroy$ = new Subject<void>();
  
  // Keyboard navigation
  highlightedIndex = -1;
  isDropdownOpen = false;

  // Multi-select state
  selectedOptions: T[] = [];

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  // Handle click outside
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeDropdown();
    }
  }

  // Handle keyboard events
  @HostListener('keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.isDropdownOpen) return;

    switch (event.key) {
      case KeyboardKeys.ArrowDown:
        event.preventDefault();
        this.highlightNext();
        break;
      case KeyboardKeys.ArrowUp:
        event.preventDefault();
        this.highlightPrevious();
        break;
      case KeyboardKeys.Enter:
        event.preventDefault();
        if (this.highlightedIndex >= 0) {
          this.onSelect(this.options[this.highlightedIndex]);
        }
        break;
      case KeyboardKeys.Escape:
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private highlightNext() {
    this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.options.length - 1);
  }

  private highlightPrevious() {
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
  }

  private closeDropdown() {
    this.isDropdownOpen = false;
    this.options = [];
    this.highlightedIndex = -1;
    this.cdr.detectChanges();
  }

  // ControlValueAccessor implementation
  private onChange: any = () => {};
  protected onTouched: any = () => {};
  private value: any;

  // Add a method to check if template exists
  hasItemTemplate(): boolean {
    return !!this.config?.itemTemplate;
  }

  ngOnInit() {
    this.setupTypeahead();
  }

  // Add helper method for dropdown positioning
  getDropdownStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {
      'width': this.config.dropdownWidth || '100%',
      'max-height': this.config.dropdownMaxHeight || '20rem'
    };

    // Add position styles
    switch (this.config.dropdownPosition) {
      case 'right':
        styles['right'] = '0';
        styles['left'] = 'auto';
        break;
      case 'center':
        styles['left'] = '50%';
        styles['transform'] = 'translateX(-50%)';
        break;
      case 'left':
      default:
        styles['left'] = '0';
        styles['right'] = 'auto';
    }

    return styles;
  }

  // Add a simpler version for the "No Results" dropdown
  getNoResultsStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {
      'width': this.config.dropdownWidth || '100%'
    };

    // Add position styles
    switch (this.config.dropdownPosition) {
      case 'right':
        styles['right'] = '0';
        styles['left'] = 'auto';
        break;
      case 'center':
        styles['left'] = '50%';
        styles['transform'] = 'translateX(-50%)';
        break;
      case 'left':
      default:
        styles['left'] = '0';
        styles['right'] = 'auto';
    }

    return styles;
  }

  private setupTypeahead() {
    this.searchControl.valueChanges.pipe(
      debounceTime(this.config.debounceTime || 300),
      distinctUntilChanged(),
      tap(term => {
        if (term && typeof term === 'string' && term.length >= (this.config.minLength || 2)) {
          this.isLoading = true;
          this.isDropdownOpen = true;
          this.cdr.detectChanges();
        } else {
          this.closeDropdown();
          this.cdr.detectChanges();
        }
      }),
      filter(term => {
        if (!term || typeof term !== 'string') {
          return false;
        }
        return term.length >= (this.config.minLength || 2);
      }),
      switchMap(term => this.config.searchFn(term as string)),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (items) => {
        this.options = items.map((item, index) => ({
          item,
          displayText: this.config.displayFn(item),
          index
        }));
        this.isLoading = false;
        this.isDropdownOpen = true; // Always show dropdown when we have results
        this.highlightedIndex = this.options.length > 0 ? 0 : -1;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.closeDropdown();
        this.cdr.detectChanges();
      }
    });
  }

  // Add new method for handling item clicks
  handleCheckboxClick(option: TypeAheadOption<T>, event: MouseEvent) {
    event.stopPropagation();
    if (this.config.multiSelect) {
      const idx = this.selectedOptions.findIndex(sel => this.config.valueFn(sel) === this.config.valueFn(option.item));
      if (idx > -1) {
        this.selectedOptions.splice(idx, 1);
      } else {
        this.selectedOptions.push(option.item);
      }
      this.selectedMultiple.emit(this.selectedOptions.slice());
      this.onChange(this.selectedOptions.slice());
      this.onTouched();
      // Do not close dropdown in multi-select
    } else {
      this.onSelect(option);
    }
  }

  onSelect(option: TypeAheadOption<T>) {
    const value = this.config.valueFn(option.item);
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.selected.emit(option.item);
    if (this.config.clearOnSelect) {
      this.searchControl.setValue('', { emitEvent: false });
    } else {
      this.searchControl.setValue(option.displayText, { emitEvent: false });
    }
    this.closeDropdown();
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    this.value = value;
    if (value) {
      // You might need to implement reverse lookup here
      // to display the correct text for the selected value
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Add new method to handle focus
  @HostListener('focus', ['$event'])
  handleFocus() {
    if (this.options.length > 0) {
      this.isDropdownOpen = true;
      this.cdr.detectChanges();
    }
  }

  isOptionChecked(option: TypeAheadOption<T>, index: number): boolean {
    if (this.config?.multiSelect) {
      return this.selectedOptions.some(sel => this.config.valueFn(sel) === this.config.valueFn(option.item));
    } else {
      return index === this.highlightedIndex;
    }
  }

  // Returns true if all options are selected
  areAllOptionsSelected(): boolean {
    if (!this.config?.multiSelect || !this.options.length) return false;
    return this.options.every(opt => this.selectedOptions.some(sel => this.config.valueFn(sel) === this.config.valueFn(opt.item)));
  }

  // Toggles select all/deselect all
  toggleSelectAll(): void {
    if (this.areAllOptionsSelected()) {
      // Deselect all
      this.selectedOptions = [];
    } else {
      // Select all
      this.selectedOptions = this.options.map(opt => opt.item);
    }
    this.selectedMultiple.emit(this.selectedOptions.slice());
    this.onChange(this.selectedOptions.slice());
    this.onTouched();
  }
}
